<script setup>
/**
 * 一次调用的详情。**做成抽屉不是新页面** —— 排障时要连着看好几条（尤其是链上的两步），
 * 每看一条就整页跳走再退回来，人会丢掉列表的位置和筛选条件。
 *
 * 抽屉里五块，按「查一次调用」的顺序排：指标 → 内容 → 计费 → 归因 → 这一轮的降级过程。
 * 链上任一步都能点进去，抽屉原地换一条，列表不动。
 *
 * 每一块是一段带小标题的内容，**不是一个圆角盒子** —— 抽屉本身已经是一层面，再套盒子就是盒中盒。
 */
import { computed, ref, watch } from 'vue'
import { Badge, Button, Drawer, Spinner, Stat } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { getUsageCall } from '../../../api/usage.js'
import { formatDateTime, formatDuration, formatOptionalInt } from '../llm-format.js'
import CallBilling from './CallBilling.vue'
import CallChain from './CallChain.vue'
import LongText from '../../../components/LongText.vue'
import StoryCallBusiness from './StoryCallBusiness.vue'

const props = defineProps({
  open: { type: Boolean, default: false },
  /** 要看哪一条；换一个值就重新拉。 */
  requestId: { type: String, default: '' },
})

const emit = defineEmits(['update:open', 'open'])

const { t } = useI18n()

const detail = ref(null)
const loading = ref(false)
const error = ref('')

watch(
  () => [props.open, props.requestId],
  () => {
    if (props.open && props.requestId) load()
  },
  { immediate: true },
)

async function load() {
  loading.value = true
  error.value = ''
  detail.value = null
  try {
    const call = await getUsageCall('text', props.requestId)
    detail.value = call.detail
  } catch (err) {
    // 后端的错误原样说出来，不吞、不改写成「加载失败」。脏行（路由归因三列只写了一部分）不走这里：
    // 它是 200 + `route.state = 'broken'`，正常渲染 —— 最需要后台的时候不能给人一个白屏。
    error.value = err.message
  } finally {
    loading.value = false
  }
}

const requestText = computed(() => detail.value === null ? '' : JSON.stringify({
  model: detail.value.call.model,
  messages: detail.value.content.messages,
}, null, 2))

const responseText = computed(() => {
  if (detail.value === null) return ''
  switch (detail.value.call.status) {
    case 'ok':
      return JSON.stringify({
        content: detail.value.content.response_text,
        reasoning: detail.value.content.reasoning_text,
      }, null, 2)
    case 'error':
    case 'aborted':
      return ''
    default:
      throw new Error(`Unhandled LLM call status: ${detail.value.call.status}`)
  }
})

const errorDetailText = computed(() => (
  detail.value === null || detail.value.error === null
    ? ''
    : JSON.stringify(detail.value.error.detail, null, 2)
))

</script>

<template>
  <Drawer
    :open="open"
    width="min(920px, 92vw)"
    :title="t('visualGameAdmin.llm.calls.detail.title', { id: requestId })"
    @update:open="emit('update:open', $event)"
  >
    <div
      v-if="loading"
      class="call-detail__state"
    >
      <Spinner :label="t('visualGameAdmin.common.loading')" />
      <span>{{ t('visualGameAdmin.common.loading') }}</span>
    </div>

    <div
      v-else-if="error"
      class="call-detail__state"
    >
      <p class="vga-error">
        {{ error }}
      </p>
      <Button
        variant="secondary"
        @click="load"
      >
        {{ t('visualGameAdmin.common.retry') }}
      </Button>
    </div>

    <div
      v-else-if="detail"
      class="call-detail"
    >
      <StoryCallBusiness v-if="detail.business" :business="detail.business" />
      <section
        class="call-detail__block"
        data-insp-path="apps/visual-game-admin/src/pages/llm/call/CallDetailDrawer.vue#metrics"
      >
        <header class="call-detail__block-head">
          <h3>{{ t('visualGameAdmin.storyCallBusiness.model_status') }}</h3>
          <Badge :variant="detail.call.status === 'ok' ? 'success' : detail.call.status === 'aborted' ? 'warning' : 'danger'">
            {{ t(`visualGameAdmin.llm.calls.status.${detail.call.status}`) }}
          </Badge>
        </header>

        <div class="call-detail__stats">
          <Stat
            :label="t('visualGameAdmin.llm.calls.detail.metrics.duration')"
            :value="formatDuration(detail.call.duration_ms)"
          />
          <Stat
            :label="t('visualGameAdmin.llm.calls.detail.metrics.ttft')"
            :value="detail.call.ttft_ms === null ? '—' : formatDuration(detail.call.ttft_ms)"
          />
          <Stat
            :label="t('visualGameAdmin.llm.calls.detail.metrics.prompt_tokens')"
            :value="formatOptionalInt(detail.call.prompt_tokens)"
          />
          <Stat
            :label="t('visualGameAdmin.llm.calls.detail.metrics.cached_tokens')"
            :value="formatOptionalInt(detail.call.cached_tokens)"
          />
          <Stat
            :label="t('visualGameAdmin.llm.calls.detail.metrics.cache_write_tokens')"
            :value="formatOptionalInt(detail.call.cache_write_tokens)"
          />
          <Stat
            :label="t('visualGameAdmin.llm.calls.detail.metrics.completion_tokens')"
            :value="formatOptionalInt(detail.call.completion_tokens)"
          />
        </div>

        <dl class="call-detail__facts">
          <div>
            <dt>{{ t('visualGameAdmin.llm.calls.detail.metrics.time') }}</dt>
            <dd class="mono">
              {{ formatDateTime(detail.call.created_at) }}
            </dd>
          </div>
          <div>
            <dt>{{ t('visualGameAdmin.llm.calls.detail.metrics.provider') }}</dt>
            <dd>{{ detail.call.provider_display_name }} · {{ detail.call.provider_capability_code }}</dd>
          </div>
          <div>
            <dt>{{ t('visualGameAdmin.llm.calls.detail.metrics.model') }}</dt>
            <dd class="mono">
              {{ detail.call.model }}
            </dd>
          </div>
          <div v-if="detail.error">
            <dt>{{ t('visualGameAdmin.llm.calls.detail.metrics.errorReason') }}</dt>
            <dd class="vga-error">
              {{ detail.error.reason }} · {{ detail.error.message }}
            </dd>
          </div>
          <div>
            <dt>{{ t('visualGameAdmin.llm.calls.detail.metrics.user') }}</dt>
            <dd class="mono">
              {{ detail.call.user_id ?? '—' }}
            </dd>
          </div>
          <div>
            <dt>{{ t('visualGameAdmin.llm.calls.detail.metrics.session') }}</dt>
            <dd class="mono">
              {{ detail.call.session_id ?? '—' }}
            </dd>
          </div>
          <div>
            <dt>{{ t('visualGameAdmin.llm.calls.detail.metrics.requestId') }}</dt>
            <dd class="mono">
              {{ detail.call.request_id }}
            </dd>
          </div>
        </dl>
      </section>

      <section
        class="call-detail__block"
        data-insp-path="apps/visual-game-admin/src/pages/llm/call/CallDetailDrawer.vue#content"
      >
        <header class="call-detail__block-head">
          <h3>{{ t('visualGameAdmin.llm.calls.detail.request') }}</h3>
        </header>

        <LongText
          label="JSON"
          :text="requestText"
          :copy-text="requestText"
        />
      </section>

      <section v-if="detail.call.status === 'ok'" class="call-detail__block">
        <header class="call-detail__block-head">
          <h3>{{ t('visualGameAdmin.llm.calls.detail.response') }}</h3>
        </header>
        <LongText
          label="JSON"
          :text="responseText"
          :copy-text="responseText"
        />
      </section>

      <section v-else class="call-detail__block">
        <header class="call-detail__block-head">
          <h3>{{ t('visualGameAdmin.llm.calls.detail.error.title') }}</h3>
        </header>
        <dl class="call-detail__facts">
          <div><dt>{{ t('visualGameAdmin.llm.calls.detail.error.category') }}</dt><dd>{{ detail.error.reason }}</dd></div>
          <div><dt>{{ t('visualGameAdmin.llm.calls.detail.error.message') }}</dt><dd class="vga-error">{{ detail.error.message }}</dd></div>
        </dl>
        <LongText
          :label="t('visualGameAdmin.llm.calls.detail.error.detail')"
          :text="errorDetailText"
          :copy-text="errorDetailText"
        />
      </section>

      <CallBilling
        :billing="detail.billing"
        :user-id="detail.call.user_id ?? ''"
      />

      <section
        class="call-detail__block"
        data-insp-path="apps/visual-game-admin/src/pages/llm/call/CallDetailDrawer.vue#attribution"
      >
        <header class="call-detail__block-head">
          <h3>{{ t('visualGameAdmin.llm.calls.detail.attribution.title') }}</h3>
        </header>

        <dl class="call-detail__facts">
          <div>
            <dt>{{ t('visualGameAdmin.llm.calls.detail.attribution.key') }}</dt>
            <dd v-if="detail.attribution.key.state === 'from_key_pool'">
              <span class="mono">{{ detail.attribution.key.key.api_key_masked }}</span>
              {{ detail.attribution.key.key.label ?? t('visualGameAdmin.llm.calls.key.noLabel') }}
            </dd>
            <dd v-else-if="detail.attribution.key.state === 'key_deleted'">
              {{ t('visualGameAdmin.llm.calls.key.deleted', { id: detail.attribution.key.key_id }) }}
            </dd>
            <!-- key_id IS NULL：还没租到密钥就失败了。成功却没有 key 的行是脏的（网关写不出来），标红。 -->
            <dd v-else-if="detail.attribution.key.state === 'no_key_leased'">
              {{ t('visualGameAdmin.llm.calls.key.noKeyLeased') }}
            </dd>
            <dd
              v-else
              class="vga-error"
            >
              {{ t('visualGameAdmin.llm.calls.key.broken') }}
            </dd>
          </div>

          <div>
            <dt>{{ t('visualGameAdmin.llm.calls.detail.attribution.route') }}</dt>
            <dd v-if="detail.attribution.route.state === 'routed'">
              {{ t('visualGameAdmin.llm.calls.route.routed', { mode: detail.attribution.route.mode }) }}
            </dd>
            <dd v-else-if="detail.attribution.route.state === 'not_routed'">
              {{ t('visualGameAdmin.llm.calls.route.notRouted') }}
            </dd>
            <dd
              v-else
              class="vga-error"
            >
              {{ t('visualGameAdmin.llm.calls.route.broken') }}
            </dd>
          </div>

          <div v-if="detail.attribution.route.state === 'routed'">
            <dt>correlation_id</dt>
            <dd class="mono">
              {{ detail.attribution.route.correlation_id }}
            </dd>
          </div>
        </dl>

        <!-- 脏行。**如实报出这一行实际存的是什么** —— 哪几列有值、值是多少、哪几列是 NULL。
             不编默认值、不显示成正常：运营点开一条坏行，要的就是这几个字。 -->
        <dl
          v-if="detail.attribution.route.state === 'broken'"
          class="call-detail__facts"
        >
        </dl>
      </section>

      <CallChain
        v-if="detail.chain && detail.chain.attempts.length > 1"
        :chain="detail.chain"
        @open="emit('open', $event)"
      />
    </div>
  </Drawer>
</template>

<style scoped>
.call-detail {
  display: grid;
  gap: var(--drama-space-lg);
  min-width: 0;
}

/* 块之间靠一条分隔线（计费块、链块是子组件，选它们的根元素）。 */
.call-detail > * + * {
  padding-top: var(--drama-space-xl);
  border-top: 1px solid var(--drama-color-border);
}

.call-detail__state {
  display: grid;
  justify-items: start;
  gap: var(--drama-space-md);
  color: var(--drama-color-text-muted);
}

.call-detail__block {
  display: grid;
  gap: var(--drama-space-md);
  min-width: 0;
}

.call-detail__block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-md);
}

.call-detail__block-head h3 {
  margin: 0;
  font-family: var(--drama-font-family-heading);
  font-size: var(--drama-font-size-lg);
  font-weight: var(--drama-font-weight-medium);
}

.call-detail__stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: var(--drama-space-md);
}

.call-detail__facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--drama-space-md);
  margin: 0;
}

.call-detail__facts dt {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.call-detail__facts dd {
  margin: var(--drama-space-xs) 0 0;
  color: var(--drama-color-text);
  font-size: var(--drama-font-size-sm);
  overflow-wrap: anywhere;
}

.call-detail__none {
  margin: 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.mono {
  font-family: var(--drama-font-family-mono);
  font-variant-numeric: tabular-nums;
}
</style>
